import { useState, useCallback } from 'react';
import { MapLayerMouseEvent } from 'react-map-gl';

export interface LayerProcessor<T> {
  prefix: string;
  isVisible: boolean;
  extractId: (layerId: string) => string;
  buildFeature: (id: string) => T | null;
}

export interface HoverInfo<T> {
  longitude: number;
  latitude: number;
  features: T[];
}

/**
 * Custom hook for handling map hover interactions across multiple layers.
 * Processes map layer mouse events and manages hover popup state.
 *
 * @template T
 * @param {Array<LayerProcessor<T>>} layerProcessors - Array of layer processors defining how to extract features from map layers
 * @return {object} Hover state and event handlers for map interactions
 */
export const useMapHoverHandler = <T extends { id: string }>(
  layerProcessors: LayerProcessor<T>[]
) => {
  const [hoverInfo, setHoverInfo] = useState<HoverInfo<T> | null>(null);

  const onMouseMove = useCallback(
    (event: MapLayerMouseEvent) => {
      const { features, lngLat } = event;
      if (!features || features.length === 0) {
        setHoverInfo(null);
        return;
      }

      const geometryFeatures: T[] = [];
      const seen = new Set<string>();

      features.forEach((feature) => {
        if (!feature.layer || !feature.layer.id) return;

        const layerId = feature.layer.id;

        for (const processor of layerProcessors) {
          if (layerId.startsWith(processor.prefix) && processor.isVisible) {
            const id = processor.extractId(layerId);
            if (!seen.has(id)) {
              const builtFeature = processor.buildFeature(id);
              if (builtFeature) {
                geometryFeatures.push(builtFeature);
                seen.add(id);
              }
            }
            break;
          }
        }
      });

      if (geometryFeatures.length > 0) {
        setHoverInfo({
          longitude: lngLat.lng,
          latitude: lngLat.lat,
          features: geometryFeatures,
        });
      } else {
        setHoverInfo(null);
      }
    },
    [layerProcessors]
  );

  const onMouseLeave = useCallback(() => {
    setHoverInfo(null);
  }, []);

  return { hoverInfo, onMouseMove, onMouseLeave };
};
